/* Product delivery estimates use warehouse civil dates, never the shopper's timezone. */
(() => {
  const parseRate = value => {
    const text = String(value ?? '').trim().replace(',', '.');
    if (!/^\d+(?:\.\d+)?$/.test(text)) return null;
    const number = Number(text);
    return Number.isFinite(number) ? number : null;
  };
  const parseDays = value => new Set(String(value || '').split(',').map(Number).filter(n => n >= 1 && n <= 7));
  const parseHolidays = value => new Set(String(value || '').split(/[\s,;]+/).filter(s => /^\d{4}-\d{2}-\d{2}$/.test(s)));
  const key = date => date.toISOString().slice(0,10);
  const next = date => new Date(date.getTime() + 86400000);
  function working(date, days, holidays) { return days.has(date.getUTCDay() || 7) && !holidays.has(key(date)); }
  function advance(date, count, days, holidays) {
    if (!days.size) throw new Error('No working days configured');
    let result = new Date(date), remaining = count;
    for (let guard = 0; remaining > 0 && guard < 400; guard++) {
      result = next(result);
      if (working(result, days, holidays)) remaining--;
    }
    if (remaining) throw new Error('No available working date');
    return result;
  }
  function estimate(now, config, route) {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {timeZone: config.timezone, year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',hourCycle:'h23'}).formatToParts(now).map(p=>[p.type,p.value]));
    const today = new Date(`${parts.year}-${parts.month}-${parts.day}T00:00:00Z`);
    const cutoff = String(route.cutoff || config.cutoff).match(/^(\d{2}):(\d{2})$/);
    if (!cutoff || +cutoff[1] > 23 || +cutoff[2] > 59) throw new Error('Invalid cutoff');
    const days = parseDays(config.workdays), holidays = parseHolidays(config.holidays);
    const deliveryDays = parseDays(route.days), deliveryHolidays = parseHolidays(route.holidays);
    let dispatch = today;
    if (!working(today, days, holidays) || +parts.hour * 60 + +parts.minute >= +cutoff[1] * 60 + +cutoff[2]) dispatch = advance(today, 1, days, holidays);
    dispatch = advance(dispatch, Math.max(0, Number(config.delay) || 0), days, holidays);
    const min = Math.max(1, Number(route.min) || 1), max = Math.max(min, Number(route.max) || min);
    return {today:key(today), dispatch:key(dispatch), start:key(advance(dispatch,min,deliveryDays,deliveryHolidays)), end:key(advance(dispatch,max,deliveryDays,deliveryHolidays))};
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {estimate,parseDays,parseHolidays,parseRate};
  if (typeof customElements === 'undefined') return;
  const read = (el, selector) => JSON.parse(el.querySelector(selector).textContent);
  if (!customElements.get('yoko-delivery')) {
    customElements.define('yoko-delivery', class extends HTMLElement {
      connectedCallback() {
        this.abort = new AbortController();
        try { this.config = read(this,'[data-delivery-config]'); this.variant = read(this,'[data-yoko-variant]'); } catch { return; }
        this.dialog = this.querySelector('dialog');
        this.opener = this.querySelector('[data-delivery-open]');
        const signal = this.abort.signal;
        const open = () => { this.refresh(); if(this.dialog && !this.dialog.open) this.dialog.showModal(); };
        this.opener?.addEventListener('click',open,{signal});
        this.querySelector('[data-delivery-close]')?.addEventListener('click',()=>this.dialog.close(),{signal});
        this.dialog?.addEventListener('click',e=>{
          const r=this.dialog.getBoundingClientRect();
          if(e.target===this.dialog && (e.clientX<r.left || e.clientX>r.right || e.clientY<r.top || e.clientY>r.bottom)) this.dialog.close();
        },{signal});
        this.dialog?.addEventListener('close',()=>this.opener?.focus({preventScroll:true}),{signal});
        document.addEventListener('shopify:block:select',e=>{
          if(this.contains(e.target)) open();
        },{signal});
        this.info = this.closest('product-info');
        this.onChange = e => { if(e.target.closest('variant-selects')) {this.pending = true; this.refresh();} };
        this.info?.addEventListener('change',this.onChange,{signal:this.abort.signal});
        if(typeof subscribe === 'function') this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if(String(event.data.sectionId) !== this.dataset.section) return;
          const source = event.data.html.getElementById(this.id);
          if(source) {this.variant = read(source,'[data-yoko-variant]');this.pending = false;this.refresh();}
        });
        this.timer = setInterval(()=>this.refresh(),60000);
        document.addEventListener('visibilitychange',()=>this.refresh(),{signal:this.abort.signal});
        this.refresh();
      }
      disconnectedCallback() {if(this.dialog?.open)this.dialog.close();this.abort?.abort();this.unsubscribe?.();clearInterval(this.timer);}
      refresh() {
        if(!this.config || !this.variant) return;
        const c=this.config,l=c.labels, routes=[...this.querySelectorAll('[data-delivery-route]')];
        this.hidden = this.variant.shipping === false;
        this.querySelector('[data-delivery-fallback]').hidden = routes.length > 0;
        const status=this.querySelector('[data-dispatch]'); status.hidden=true;
        const ready=this.variant.ready && !this.pending;
        const formatDate = value => new Intl.DateTimeFormat(c.locale,{day:'numeric',month:'short',timeZone:'UTC'}).format(new Date(value+'T00:00:00Z'));
        const money = value => new Intl.NumberFormat(c.locale,{style:'currency',currency:c.currency}).format(value);
        const dispatches=[];
        routes.forEach(row=>{
          const d=row.dataset, arrival=row.querySelector('[data-arrival]'), free=row.querySelector('[data-free-label]'), rate=row.querySelector('[data-rate-label]'), dispatch=row.querySelector('[data-route-dispatch]');
          dispatch.hidden=true;
          const cutoff=row.querySelector('[data-cutoff-label]');
          if(cutoff) cutoff.textContent=`${l.cutoff} ${d.cutoff || c.cutoff} (${c.timezone})`;
          const min=Math.max(1,+d.min||1),max=Math.max(min,+d.max||min);
          arrival.textContent=`${min}–${max} ${l.days}`;
          const currencyMatches=d.currency === c.currency, threshold=+d.free;
          const amount = parseRate(d.rate);
          rate.textContent=currencyMatches && amount !== null ? money(amount) : '';
          free.hidden=!(currencyMatches && threshold > 0);
          if(!free.hidden) free.textContent=`${l.free_from} ${money(threshold)}`;
          if(ready && c.dates) {
            try {
              const dates=estimate(new Date(),c,d);
              arrival.textContent=`${l.arrival} ${formatDate(dates.start)}${dates.end===dates.start?'':' – '+formatDate(dates.end)}`;
              const text=`${l.dispatch} ${dates.dispatch===dates.today?l.today:formatDate(dates.dispatch)}`;
              dispatch.textContent=text;dispatches.push({el:dispatch,text,date:dates.dispatch});
            } catch { /* Invalid editor settings retain the static, non-promissory lead time. */ }
          }
        });
        if(!ready && routes.length) {status.textContent=l.pending;status.hidden=false;}
        else if(dispatches.length && dispatches.every(d=>d.text===dispatches[0].text)) {status.textContent=dispatches[0].text;status.hidden=false;}
        else dispatches.forEach(d=>{d.el.hidden=false;});
        // Show the earliest dispatch; the adjacent details control opens courier timings.
        // Reuse the drawer's estimates and readiness rules.
        const summaryDispatch=this.querySelector('[data-summary-dispatch]');
        if(summaryDispatch) {
          const earliest=dispatches.reduce((best,d)=>!best || d.date<best.date?d:best,null);
          summaryDispatch.hidden=!ready || !c.dates || !earliest;
          summaryDispatch.textContent=earliest ? earliest.text : '';
        }
        // Keep price, dates and free-delivery threshold tied to the same route.
        const priced=routes.filter(r=>r.dataset.currency===c.currency && parseRate(r.dataset.rate)!==null);
        const selected=priced.sort((a,b)=>parseRate(a.dataset.rate)-parseRate(b.dataset.rate))[0] || routes[0];
        const summaryRate=this.querySelector('[data-summary-rate]'), summaryArrival=this.querySelector('[data-summary-arrival]'), summaryFree=this.querySelector('[data-summary-free]');
        if(summaryRate) summaryRate.textContent=selected?.querySelector('[data-rate-label]').textContent || '';
        if(summaryArrival) summaryArrival.textContent=selected ? (ready ? selected.querySelector('[data-arrival]').textContent : l.pending) : this.querySelector('[data-delivery-fallback]').textContent;
        if(summaryFree) {const free=selected?.querySelector('[data-free-label]');summaryFree.hidden=!free || free.hidden;summaryFree.textContent=free?.textContent || '';}
        if(this.hidden && this.dialog?.open) this.dialog.close();
      }
    });
  }
  if (!customElements.get('yoko-sticky-buy')) {
    customElements.define('yoko-sticky-buy', class extends HTMLElement {
      connectedCallback() {
        this.abort=new AbortController();const signal=this.abort.signal;
        this.info=this.closest('product-info');
        this.original=document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
        if(!this.info || !this.original) return;
        this.button=this.querySelector('[data-sticky-submit]');
        this.price=this.querySelector('[data-sticky-price]');
        if(!this.button || !this.price) return;
        this.mobile=matchMedia('(max-width:767px)');
        this.aboveViewport=false;
        // Update purchase content only when it changes, never on scroll.
        this.sync=()=>{
          const disabled=this.original.disabled || this.original.getAttribute('aria-disabled')==='true';
          const label=this.original.querySelector('.txt')?.textContent.trim() || this.original.textContent.trim();
          const price=this.info.querySelector('.priceSingle .price-item')?.textContent.trim() || '';
          if(this.button.disabled!==disabled) this.button.disabled=disabled;
          if(this.button.textContent!==label) this.button.textContent=label;
          if(this.price.textContent!==price) this.price.textContent=price;
        };
        this.updateVisibility=()=>{
          const focused=document.activeElement;
          const editing=focused && /INPUT|TEXTAREA|SELECT/.test(focused.tagName);
          const hidden=!(this.mobile.matches && this.aboveViewport && !editing);
          if(this.hidden!==hidden) this.hidden=hidden;
        };
        this.measureVisibility=()=>{
          this.aboveViewport=this.mobile.matches && this.original.getBoundingClientRect().bottom < 0;
          this.updateVisibility();
        };
        this.scheduleVisibility=()=>{
          if(this.visibilityFrame) return;
          this.visibilityFrame=requestAnimationFrame(()=>{this.visibilityFrame=0;this.measureVisibility();});
        };
        this.button.addEventListener('click',()=>{if(!this.button.disabled)this.original.click();},{signal});
        this.observer=new MutationObserver(this.sync);
        this.observer.observe(this.original,{subtree:true,childList:true,attributes:true,attributeFilter:['disabled','aria-disabled']});
        const price=this.info.querySelector('[id^="price-"]');if(price)this.observer.observe(price,{childList:true,subtree:true});
        if(typeof IntersectionObserver==='function') {
          this.intersection=new IntersectionObserver(entries=>{
            const entry=entries.find(entry=>entry.target===this.original);
            if(!entry) return;
            this.aboveViewport=entry.boundingClientRect.bottom < 0;
            this.updateVisibility();
          });
          this.intersection.observe(this.original);
        } else {
          window.addEventListener('scroll',this.scheduleVisibility,{passive:true,signal});
        }
        window.addEventListener('resize',this.scheduleVisibility,{passive:true,signal});
        this.mobile.addEventListener('change',this.scheduleVisibility,{signal});
        document.addEventListener('focusin',this.updateVisibility,{signal});
        document.addEventListener('focusout',this.updateVisibility,{signal});
        this.sync();this.measureVisibility();
      }
      disconnectedCallback(){this.abort?.abort();this.observer?.disconnect();this.intersection?.disconnect();cancelAnimationFrame(this.visibilityFrame);this.visibilityFrame=0;}
    });
  }
  if(typeof subscribe === 'function' && !window.yokoProductVariantSync) {
    window.yokoProductVariantSync=true;
    subscribe(PUB_SUB_EVENTS.variantChange,event=>{
      const info=document.getElementById(`MainProduct-${event.data.sectionId}`);
      if(!info) return;
      document.querySelectorAll('[data-yoko-product-sku]').forEach(el=>{el.textContent=event.data.variant.sku || '—';});
      info.querySelectorAll('.se-product-wishlist .wishlist-engine').forEach(el=>{el.dataset.variant_id=event.data.variant.id;});
    });
  }
  // Wishlist Engine owns persistence and click handling; add keyboard parity to its div control.
  if(!window.yokoWishlistKeyboard) {
    window.yokoWishlistKeyboard=true;
    document.addEventListener('keydown',event=>{
      const target=event.target.closest('.se-product-wishlist .wishlist-engine[role="button"]');
      if(target && (event.key==='Enter' || event.key===' ')){event.preventDefault();target.click();}
    });
  }
})();

